import { useState, useEffect, useCallback } from 'react'
import { RefreshCw, AlertCircle, CheckCircle, AlertTriangle, XCircle, ChevronDown, ChevronUp } from 'lucide-react'
import { fmtUSD, fmtUSD2, fmtDate, fmtNum } from '../lib/format'
import {
  getActiveSubscriptions,
  getChargesThisMonth,
  getRevenueSummary,
  getPaymentPlanReconciliation,
  getPlanHistory,
  getRefunds,
  markLostIncome,
  getPlanStates,
  setPlanState,
  getLostIncomeRecords,
} from '../lib/stripe'

// ─── Helpers ────────────────────────────────────────────────────────────────

function useAsync(fn, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null })
  const run = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }))
    try {
      const data = await fn()
      setState({ data, loading: false, error: null })
    } catch (e) {
      setState({ data: null, loading: false, error: e.message })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  useEffect(() => { run() }, [run])
  return { ...state, refresh: run }
}

function LoadRow({ label }) {
  return (
    <div className="loading-center" style={{ padding: '32px' }}>
      <div className="spinner" />
      <span>Loading {label}…</span>
    </div>
  )
}

function ErrRow({ message, onRetry }) {
  return (
    <div className="loading-center" style={{ gap: 8, flexDirection: 'column' }}>
      <AlertCircle size={20} color="var(--danger)" />
      <span style={{ color: 'var(--danger)', fontSize: 13 }}>{message}</span>
      {onRetry && <button className="btn btn-ghost" onClick={onRetry}>Retry</button>}
    </div>
  )
}

function MismatchBadge({ status }) {
  if (status === 'ok')      return <span className="badge badge-green"><CheckCircle size={10} /> In Sync</span>
  if (status === 'ahead')   return <span className="badge badge-teal"><AlertTriangle size={10} /> Stripe Ahead</span>
  return                           <span className="badge badge-red"><XCircle size={10} /> Data Issue</span>
}


// ─── Sub-sections ────────────────────────────────────────────────────────────

function ExpectedIncomePanel({ refreshKey }) {
  const { data, loading, error, refresh } = useAsync(() =>
    Promise.all([getActiveSubscriptions(), getChargesThisMonth()])
      .then(([subs, charges]) => ({ subs, charges })),
  [refreshKey])

  if (loading) return <LoadRow label="expected income" />
  if (error)   return <ErrRow message={error} onRetry={refresh} />

  const { subs, charges } = data

  const paymentPlanTotal   = subs.paymentPlanThisMonth  ?? 0
  const subscriptionMRR    = subs.subscriptionMRR       ?? 0
  const totalExpected      = paymentPlanTotal + subscriptionMRR
  const receivedSoFar      = charges.receivedThisMonth  ?? 0
  const remaining          = Math.max(0, totalExpected - receivedSoFar)

  const rows = [
    { label: 'Payment plan income this month',  value: paymentPlanTotal,  note: 'Rebills only (payment 2+) due this month — initial payments excluded' },
    { label: 'Subscription MRR',                value: subscriptionMRR,   note: 'All active recurring subscribers — annuals ÷ 12, 10-month plans ÷ 10' },
    { label: 'Total expected this month',        value: totalExpected,     highlight: true },
    { label: 'Received so far this month',       value: receivedSoFar,     color: 'teal', note: 'Rebills only — first-time purchases excluded' },
    { label: 'Remaining expected',               value: remaining,         color: remaining > 0 ? 'default' : 'success' },
  ]

  return (
    <div className="card" style={{ gridColumn: 'span 2' }}>
      <div className="section-header" style={{ marginBottom: 20 }}>
        <h3 className="section-title">Expected Revenue This Month</h3>
        <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={refresh}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map(({ label, value, note, highlight, color }) => (
          <div key={label} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            paddingBottom: 12,
            borderBottom: highlight ? '2px solid var(--gold)' : '1px solid var(--border)',
          }}>
            <div>
              <div style={{ fontWeight: highlight ? 700 : 500, fontSize: highlight ? 15 : 14 }}>{label}</div>
              {note && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{note}</div>}
            </div>
            <div className={`metric-value${color ? ` ${color}` : ''}`} style={{ fontSize: highlight ? 28 : 22 }}>
              {fmtUSD(value)}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ActualRevenuePanel() {
  const { data, loading, error, refresh } = useAsync(getRevenueSummary)

  if (loading) return <LoadRow label="actual revenue" />
  if (error)   return <ErrRow message={error} onRetry={refresh} />

  const periods = [
    { label: 'Today',      value: data.today },
    { label: 'This Week',  value: data.week },
    { label: 'This Month', value: data.month },
  ]

  return (
    <div className="card">
      <div className="section-header">
        <h3 className="section-title">Actual Revenue Received</h3>
        <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={refresh}>
          <RefreshCw size={12} />
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
        {periods.map(({ label, value }) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span className="card-title" style={{ marginBottom: 0 }}>{label}</span>
            <span className="metric-value teal" style={{ fontSize: 24 }}>{fmtUSD(value)}</span>
          </div>
        ))}
      </div>
      {data.byProduct?.length > 0 && (
        <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <div className="card-title" style={{ marginBottom: 10 }}>Revenue by Product (this month)</div>
          {data.byProduct.map(p => (
            <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
              <span style={{ color: 'var(--text-muted)' }}>{p.name}</span>
              <span style={{ fontWeight: 600 }}>{fmtUSD(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SubscriptionPanel() {
  const { data, loading, error, refresh } = useAsync(getActiveSubscriptions)

  if (loading) return <LoadRow label="subscriptions" />
  if (error)   return <ErrRow message={error} onRetry={refresh} />

  const stats = [
    { label: 'Active Subscriptions', value: fmtNum(data.activeCount),   color: 'teal' },
    { label: 'MRR',                  value: fmtUSD(data.subscriptionMRR) },
    { label: 'New This Month',       value: fmtNum(data.newThisMonth),   color: 'teal' },
    { label: 'Churned This Month',   value: fmtNum(data.churnedThisMonth), color: data.churnedThisMonth > 0 ? 'danger' : undefined },
  ]

  return (
    <div className="card">
      <div className="section-header">
        <h3 className="section-title">Subscription Revenue</h3>
        <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={refresh}>
          <RefreshCw size={12} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 12 }}>
        {stats.map(({ label, value, color }) => (
          <div key={label}>
            <div className="card-title">{label}</div>
            <div className={`metric-value${color ? ` ${color}` : ''}`} style={{ fontSize: 24 }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Reconciliation Table ─────────────────────────────────────────────────────

function StatusToggle({ status, onChange, disabled }) {
  const options = [
    { key: 'active', label: 'Active' },
    { key: 'paused', label: 'Paused' },
    { key: 'lost',   label: 'Lost' },
  ]
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {options.map(o => (
        <button
          key={o.key}
          disabled={disabled}
          onClick={() => onChange(o.key)}
          className={`btn ${status === o.key ? (o.key === 'lost' ? 'btn-danger' : 'btn-primary') : 'btn-ghost'}`}
          style={{ padding: '3px 8px', fontSize: 10 }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function LostConfirmForm({ customer, onCancel, onConfirm }) {
  const defaultAmount = customer.remainingInstallments != null
    ? customer.remainingInstallments * customer.installmentAmount
    : customer.installmentAmount
  const [amount, setAmount] = useState(defaultAmount.toFixed(2))
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)

  const handleConfirm = async () => {
    setSaving(true)
    try {
      await onConfirm({ amountLost: parseFloat(amount) || 0, dateLost: date, notes })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--danger)' }}>
      <div>
        <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Amount Lost</label>
        <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
          style={{ width: 100, padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4 }} />
      </div>
      <div>
        <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Date Marked Lost</label>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4 }} />
      </div>
      <div style={{ flex: 1, minWidth: 160 }}>
        <label style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notes (optional)</label>
        <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
          style={{ width: '100%', padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4 }} />
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 11 }} disabled={saving} onClick={handleConfirm}>
          {saving ? 'Saving…' : 'Confirm Lost'}
        </button>
        <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function ReconciliationRow({ customer, onMarkLost, showToggle, planState, onSetState, muted }) {
  const [expanded, setExpanded] = useState(false)
  const [marking, setMarking] = useState(false)
  const [history, setHistory] = useState(null)
  const [histLoading, setHistLoading] = useState(false)
  const [showLostForm, setShowLostForm] = useState(false)

  const handleExpand = async () => {
    const next = !expanded
    setExpanded(next)
    if (next && history === null) {
      setHistLoading(true)
      try {
        const res = await getPlanHistory({
          subId:  customer.format !== 'legacy' ? customer.id       : undefined,
          custId: customer.format === 'legacy' ? customer.customerId : undefined,
          amount: Math.round(customer.installmentAmount * 100),
          since:  undefined,
        })
        setHistory(res.history || [])
      } catch {
        setHistory([])
      } finally {
        setHistLoading(false)
      }
    }
  }

  const handleMarkLost = async () => {
    if (!confirm(`Mark ${customer.name} as lost income?`)) return
    setMarking(true)
    try {
      await markLostIncome({ customerId: customer.id, customerEmail: customer.email })
      onMarkLost(customer.id)
    } catch (e) {
      alert(`Failed: ${e.message}`)
    } finally {
      setMarking(false)
    }
  }

  const handleToggle = async (status) => {
    if (status === 'lost') { setShowLostForm(true); return }
    setShowLostForm(false)
    await onSetState(customer, status)
  }

  const handleConfirmLost = async (lostDetail) => {
    await onSetState(customer, 'lost', lostDetail)
    setShowLostForm(false)
  }

  const isSubscription = customer.kind === 'subscription'
  const knownTotal  = customer.totalInstallments != null
  const lostAmount  = knownTotal ? customer.remainingInstallments * customer.installmentAmount : null
  const totalDisplay   = knownTotal ? customer.totalInstallments   : '?'
  const remainDisplay  = knownTotal ? customer.remainingInstallments : '?'
  const totalValDisplay = knownTotal ? fmtUSD(customer.totalPlanValue) : '?'
  const lostDisplay    = lostAmount != null ? fmtUSD(lostAmount) : ''

  return (
    <>
      <tr style={muted ? { opacity: 0.55 } : undefined}>
        <td>
          <div style={{ fontWeight: 600 }}>{customer.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{customer.email}</div>
        </td>
        <td>
          <div>{customer.planType}</div>
          {customer.format === 'legacy' && (
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>Legacy plan</div>
          )}
        </td>
        <td>{fmtUSD2(customer.installmentAmount)}</td>
        <td style={{ textAlign: 'center' }}>
          {isSubscription ? (
            <span style={{ color: 'var(--teal)', fontWeight: 600 }}>{customer.stripePayments} payments</span>
          ) : (
            <>
              <span style={{ color: 'var(--teal)', fontWeight: 600 }}>{customer.stripePayments}</span>
              {' / '}
              <span style={{ color: 'var(--text-muted)' }}>{totalDisplay}</span>
            </>
          )}
        </td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <MismatchBadge status={customer.syncStatus} />
            {customer.pastDue && (
              <AlertTriangle size={14} color="var(--danger)" title="Stripe: past due" />
            )}
          </div>
        </td>
        <td>{customer.nextPaymentDate ? fmtDate(customer.nextPaymentDate) : '—'}</td>
        <td>
          {showToggle ? (
            <StatusToggle status={planState} onChange={handleToggle} />
          ) : (
            (customer.syncStatus === 'issue' || customer.status === 'paused') && (
              <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 11 }}
                onClick={handleMarkLost} disabled={marking}>
                Mark Lost {lostDisplay ? `(${lostDisplay})` : ''}
              </button>
            )
          )}
        </td>
        <td>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
            onClick={handleExpand}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </td>
      </tr>
      {showToggle && showLostForm && (
        <tr>
          <td colSpan={8} style={{ padding: '8px 16px' }}>
            <LostConfirmForm customer={customer} onCancel={() => setShowLostForm(false)} onConfirm={handleConfirmLost} />
          </td>
        </tr>
      )}
      {expanded && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--bg)', padding: '12px 16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, fontSize: 13 }}>
              {!isSubscription && <div><div className="card-title">Total Plan Value</div><strong>{totalValDisplay}</strong></div>}
              <div><div className="card-title">Payments Made</div><strong>{customer.stripePayments}</strong></div>
              {!isSubscription && <div><div className="card-title">Remaining</div><strong>{remainDisplay}</strong></div>}
              {!isSubscription && <div><div className="card-title">Remaining Value</div><strong>{lostDisplay || '?'}</strong></div>}
              <div><div className="card-title">Status</div><strong>{customer.status}</strong></div>
            </div>
            <div style={{ marginTop: 12 }}>
                <div className="card-title" style={{ marginBottom: 8 }}>Payment History</div>
                {histLoading && <div className="loading-center" style={{ padding: 16 }}><div className="spinner" /><span>Loading…</span></div>}
                {!histLoading && history !== null && history.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No payment history found</div>
                )}
                {!histLoading && history && history.length > 0 && (
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Date</th><th>Amount</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((p, i) => (
                      <tr key={i}>
                        <td>{fmtDate(p.date)}</td>
                        <td>{fmtUSD2(p.amount)}</td>
                        <td><span className={`badge badge-${p.status === 'succeeded' ? 'green' : 'red'}`}>{p.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                )}
              </div>
          </td>
        </tr>
      )}
    </>
  )
}

function ReconciliationTable({ title, list, installmentLabel, onMarkLost, showToggle, planStates, onSetState, muted }) {
  const syncOk      = list.filter(c => c.syncStatus === 'ok').length
  const syncAhead   = list.filter(c => c.syncStatus === 'ahead').length
  const syncIssue   = list.filter(c => c.syncStatus === 'issue').length

  return (
    <div>
      <div className="section-header" style={{ marginBottom: 10 }}>
        <h4 className="card-title" style={{ fontSize: 14, margin: 0 }}>{title} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({list.length})</span></h4>
        {!muted && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="badge badge-green">{syncOk} In Sync</span>
            <span className="badge badge-teal">{syncAhead} Stripe Ahead</span>
            <span className="badge badge-red">{syncIssue} Issues</span>
          </div>
        )}
      </div>
      <div style={{ background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Plan Type</th>
                <th>{title === 'Payment Plans' ? 'Installment' : 'Amount'}</th>
                <th>{installmentLabel}</th>
                <th>Sync</th>
                <th>Next Payment</th>
                <th>{showToggle ? 'Status' : 'Action'}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>None found</td></tr>
              ) : (
                list.map(c => (
                  <ReconciliationRow
                    key={c.id}
                    customer={c}
                    onMarkLost={onMarkLost}
                    showToggle={showToggle}
                    planState={showToggle ? (planStates[c.id] ?? 'active') : undefined}
                    onSetState={onSetState}
                    muted={muted}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function ReconciliationSection({ onToggleChange }) {
  const { data, loading, error, refresh } = useAsync(getPaymentPlanReconciliation)
  const { data: statesData, refresh: refreshStates } = useAsync(getPlanStates)
  const [customers, setCustomers] = useState(null)
  const [planStates, setPlanStates] = useState({})
  const [activeTab, setActiveTab] = useState('payment_plan')

  useEffect(() => {
    if (data?.customers) setCustomers(data.customers)
  }, [data])

  useEffect(() => {
    if (statesData?.states) {
      const flat = {}
      for (const [id, s] of Object.entries(statesData.states)) flat[id] = s.status
      setPlanStates(flat)
    }
  }, [statesData])

  const handleMarkLost = (id) => {
    setCustomers(cs => cs.filter(c => c.id !== id))
  }

  const handleSetState = async (customer, status, lostDetail) => {
    setPlanStates(s => ({ ...s, [customer.id]: status }))
    await setPlanState(customer.id, status)
    if (status === 'lost' && lostDetail) {
      await markLostIncome({
        customerId: customer.id,
        customerEmail: customer.email,
        planId: customer.id,
        planName: customer.planType,
        ...lostDetail,
      })
    }
    onToggleChange?.()
  }

  if (loading) return <LoadRow label="payment plan reconciliation" />
  if (error)   return <ErrRow message={error} onRetry={refresh} />

  const list = customers ?? data?.customers ?? []

  const paymentPlans = list.filter(c => c.kind === 'payment_plan')
  const subscriptions = list.filter(c => c.kind === 'subscription')

  // A payment plan's effective status: manual toggle wins; otherwise fall
  // back to whatever Stripe itself reports (paused / past_due → Paused).
  const effectiveStatusMap = {}
  for (const c of paymentPlans) {
    effectiveStatusMap[c.id] = planStates[c.id] ?? (c.stripeAutoPaused ? 'paused' : 'active')
  }

  const activePaymentPlans = paymentPlans.filter(c => effectiveStatusMap[c.id] === 'active')
  const pausedLostPaymentPlans = paymentPlans.filter(c => effectiveStatusMap[c.id] !== 'active')

  const tabs = [
    { key: 'payment_plan', label: 'Payment Plans', list: activePaymentPlans, installmentLabel: 'Paid / Total' },
    { key: 'subscription', label: 'Ongoing Subscriptions', list: subscriptions, installmentLabel: 'Payments Made' },
  ]
  const current = tabs.find(t => t.key === activeTab)

  return (
    <div>
      <div className="section-header">
        <h3 className="section-title">Payment Plan &amp; Subscription Reconciliation</h3>
        <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 11 }} onClick={() => { refresh(); refreshStates() }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`btn ${activeTab === t.key ? 'btn-primary' : 'btn-ghost'}`}
            style={{ padding: '8px 16px', borderRadius: '6px 6px 0 0', borderBottom: 'none' }}
          >
            {t.label} <span style={{ opacity: 0.7 }}>({t.list.length})</span>
          </button>
        ))}
      </div>
      <ReconciliationTable
        title={current.label}
        list={current.list}
        installmentLabel={current.installmentLabel}
        onMarkLost={handleMarkLost}
        showToggle={activeTab === 'payment_plan'}
        planStates={effectiveStatusMap}
        onSetState={handleSetState}
      />

      {activeTab === 'payment_plan' && (
        <div style={{ marginTop: 24 }}>
          <ReconciliationTable
            title="Paused / Lost"
            list={pausedLostPaymentPlans}
            installmentLabel="Paid / Total"
            showToggle
            planStates={effectiveStatusMap}
            onSetState={handleSetState}
            muted
          />
        </div>
      )}
    </div>
  )
}

// ─── Lost Income Section ──────────────────────────────────────────────────────

function LostIncomeSection({ refreshKey }) {
  const [period, setPeriod] = useState('month')
  const { data: refundsData, loading: refLoading, error: refError, refresh: refreshRef } = useAsync(
    () => getRefunds(period), [period, refreshKey]
  )
  const { data: lostData } = useAsync(getLostIncomeRecords, [refreshKey])

  const manualRecords = lostData?.records ?? []
  const manualLostTotal = manualRecords.reduce((s, r) => s + (r.amountLost ?? 0), 0)

  const totals = refundsData ? {
    failedPlans: refundsData.lostFromFailedPlans ?? 0,
    refunds: refundsData.totalRefunds ?? 0,
    manualLost: manualLostTotal,
    combined: (refundsData.lostFromFailedPlans ?? 0) + (refundsData.totalRefunds ?? 0) + manualLostTotal,
  } : null

  return (
    <div>
      <div className="section-header">
        <h3 className="section-title">Lost Income Tracking</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {['month', 'quarter', 'all'].map(p => (
            <button key={p} className={`btn ${period === p ? 'btn-primary' : 'btn-ghost'}`}
              style={{ padding: '4px 12px', fontSize: 11 }}
              onClick={() => setPeriod(p)}>
              {p === 'all' ? 'All Time' : p === 'quarter' ? 'Quarter' : 'This Month'}
            </button>
          ))}
        </div>
      </div>

      {totals && (
        <div className="grid-3" style={{ marginBottom: 20 }}>
          <div className="card">
            <div className="card-title">Failed Plans Lost</div>
            <div className="metric-value danger" style={{ fontSize: 26 }}>{fmtUSD(totals.failedPlans)}</div>
          </div>
          <div className="card">
            <div className="card-title">Refunds</div>
            <div className="metric-value danger" style={{ fontSize: 26 }}>{fmtUSD(totals.refunds)}</div>
          </div>
          <div className="card">
            <div className="card-title">Marked Lost (Manual)</div>
            <div className="metric-value danger" style={{ fontSize: 26 }}>{fmtUSD(totals.manualLost)}</div>
          </div>
          <div className="card">
            <div className="card-title">Total Lost Income</div>
            <div className="metric-value danger" style={{ fontSize: 26 }}>{fmtUSD(totals.combined)}</div>
          </div>
        </div>
      )}

      {manualRecords.length > 0 && (
        <div style={{ background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Manually Marked Lost
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Plan</th>
                  <th>Amount Lost</th>
                  <th>Date Marked Lost</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {manualRecords.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.customerEmail || r.customerId}</div>
                    </td>
                    <td>{r.planName || '—'}</td>
                    <td style={{ color: 'var(--danger)', fontWeight: 600 }}>{fmtUSD2(r.amountLost)}</td>
                    <td>{fmtDate(r.dateLost)}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{r.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {refLoading && <LoadRow label="lost income" />}
      {refError   && <ErrRow message={refError} onRetry={refreshRef} />}

      {refundsData?.refundList?.length > 0 && (
        <div style={{ background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontFamily: 'Barlow Condensed', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Refund Log
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Item</th>
                  <th>Original</th>
                  <th>Refunded</th>
                  <th>Fee Lost</th>
                  <th>Net Lost</th>
                  <th>Date</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {refundsData.refundList.map((r, i) => (
                  <tr key={i}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{r.customerName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{r.customerEmail}</div>
                    </td>
                    <td>{r.item}</td>
                    <td>{fmtUSD2(r.originalAmount)}</td>
                    <td style={{ color: 'var(--danger)' }}>{fmtUSD2(r.refundAmount)}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{fmtUSD2(r.transactionFee)}</td>
                    <td style={{ color: 'var(--danger)', fontWeight: 600 }}>{fmtUSD2(r.netLost)}</td>
                    <td>{fmtDate(r.date)}</td>
                    <td><span className="badge badge-grey">{r.source}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Profit Share Section ─────────────────────────────────────────────────────

function ProfitShareSection() {
  const [settings, setSettings] = useState({ pct: '', basis: 'gross' })
  const [saved, setSaved] = useState(false)

  const handleSave = () => {
    localStorage.setItem('profitShareSettings', JSON.stringify(settings))
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  useEffect(() => {
    const s = localStorage.getItem('profitShareSettings')
    if (s) setSettings(JSON.parse(s))
  }, [])

  return (
    <div>
      <div className="section-header">
        <h3 className="section-title">Profit Share Report</h3>
        <span className="badge badge-gold">Settings Required</span>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, fontFamily: 'Barlow Condensed', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Profit Share %
            </label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              placeholder="e.g. 25"
              value={settings.pct}
              onChange={e => setSettings(s => ({ ...s, pct: e.target.value }))}
              style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 15, width: 120, background: 'var(--bg)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, fontFamily: 'Barlow Condensed', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Apply to
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {['gross', 'net'].map(b => (
                <button key={b} className={`btn ${settings.basis === b ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ padding: '8px 16px' }}
                  onClick={() => setSettings(s => ({ ...s, basis: b }))}>
                  {b.charAt(0).toUpperCase() + b.slice(1)} Revenue
                </button>
              ))}
            </div>
          </div>
          <button className={`btn ${saved ? 'btn-gold' : 'btn-primary'}`} onClick={handleSave}>
            {saved ? 'Saved ✓' : 'Save Settings'}
          </button>
        </div>
        {!settings.pct && (
          <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(251,208,51,0.1)', borderRadius: 6, border: '1px solid rgba(251,208,51,0.3)', fontSize: 13 }}>
            Enter a profit share percentage to generate the report. All historical months will recalculate when you save.
          </div>
        )}
      </div>

      {settings.pct && (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 14 }}>
            Profit share configured at <strong>{settings.pct}%</strong> of <strong>{settings.basis}</strong> revenue.
            <br />
            Connect PayPal API credentials to activate full report. Stripe-only report available immediately.
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Revenue Tab (root) ───────────────────────────────────────────────────────

export default function Revenue() {
  const now = new Date()
  const monthName = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const [refreshKey, setRefreshKey] = useState(0)

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Revenue</h1>
          <p className="page-subtitle">{monthName} · Stripe live data</p>
        </div>
      </div>

      {/* ── Expected + Actual ── */}
      <div className="section">
        <div className="grid-2" style={{ gridTemplateColumns: '2fr 1fr', gap: 16 }}>
          <ExpectedIncomePanel refreshKey={refreshKey} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <ActualRevenuePanel />
            <SubscriptionPanel />
          </div>
        </div>
      </div>

      <div className="divider" />

      {/* ── Reconciliation ── */}
      <div className="section">
        <ReconciliationSection onToggleChange={() => setRefreshKey(k => k + 1)} />
      </div>

      <div className="divider" />

      {/* ── Lost Income ── */}
      <div className="section">
        <LostIncomeSection refreshKey={refreshKey} />
      </div>

      <div className="divider" />

      {/* ── Profit Share ── */}
      <div className="section">
        <ProfitShareSection />
      </div>
    </div>
  )
}
